import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";

const ResetMFAButton = ({ onDone }: { onDone?: () => void }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  

  const handleReset = async () => {
    setLoading(true);
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      for (const factor of factors?.totp || []) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }

      toast({
        title: t("mfa.resetSuccess"),
        description: t("mfa.resetSuccessDesc"),
      });

      setOpen(false);
      onDone?.();
      navigate("/mfa-setup");
    } catch (error: any) {
      console.error("MFA reset error:", error);
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)}>
        <ShieldAlert className="w-4 h-4" />
        {t("mfa.resetButton")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-[64px] h-[64px] rounded-2xl bg-destructive/10 flex items-center justify-center">
                  <ShieldAlert className="w-8 h-8 text-destructive" />
                </div>
                <div>
                  <DialogTitle>{t("mfa.resetTitle")}</DialogTitle>
                  <DialogDescription>{t("mfa.resetDesc")}</DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 rounded-lg p-3 my-2">
              <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive leading-relaxed">
                {t("mfa.resetWarning")}
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
                {t("common.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleReset} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("mfa.resetButton")}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResetMFAButton;
